import { useLanderConfig } from "../commons/ConfigContext";
import { isRollerAdsGeoOverride } from "../commons/HelperFunctions";
import { FeedType, LanderConfig } from "../types/LanderConfig";
import { buildRsocRedirectUrl } from "../rsoc/useRsoc";
import { useRollerAdsUsExperiment } from "../experimentation/useRollerAdsUsExperiment";

export enum AdProvider {
  RSOC = "rsoc",
  ROLLER_ADS = "roller_ads",
  YAHOO = "yahoo",
}

export interface AdProviderResult {
  provider: AdProvider;
  rsocUrl: string | null;
  rsocError: boolean;
  experimentKeywordClick: boolean;
}

function isRsocEligible(landerConfig: LanderConfig): boolean {
  const rsocPathOrUrlExists =
    !!landerConfig.rsoc?.path || !!landerConfig.rsoc?.url;
  const feedIDIsRsoc = landerConfig.lander?.feedID === FeedType.RSOC;
  const domainIsCloudynestStore =
    landerConfig.domain?.rootDomain === "cloudynest.store";

  return rsocPathOrUrlExists && feedIDIsRsoc && !domainIsCloudynestStore;
}

/**
 * Resolves which ad provider to use. Priority order:
 * 1. Geo-override (CN + the keyword-click geos) -> ROLLER_ADS
 * 2. RSOC-eligible: treatment overrides to ROLLER_ADS 1-click; otherwise RSOC
 * 3. feedID === ROLLER_ADS_1CLICK -> ROLLER_ADS with experimentKeywordClick
 * 4. feedID === ROLLER_ADS -> ROLLER_ADS (unaffected by experiment)
 * 5. Yahoo baseline: treatment overrides to ROLLER_ADS 1-click; otherwise YAHOO
 */
export function useAdProvider(): AdProviderResult {
  const { landerConfig } = useLanderConfig();
  const { inRollerAdsTreatment } = useRollerAdsUsExperiment();

  let provider: AdProvider;
  let rsocUrl: string | null = null;
  let rsocError = false;
  let experimentKeywordClick = false;

  if (isRollerAdsGeoOverride()) {
    provider = AdProvider.ROLLER_ADS;
  } else if (isRsocEligible(landerConfig)) {
    if (inRollerAdsTreatment) {
      provider = AdProvider.ROLLER_ADS;
      experimentKeywordClick = true;
    } else {
      const result = buildRsocRedirectUrl(landerConfig);
      if (result.url) {
        provider = AdProvider.RSOC;
        rsocUrl = result.url;
      } else {
        rsocError = true;
        provider = AdProvider.YAHOO;
      }
    }
  } else if (landerConfig.lander?.feedID === FeedType.ROLLER_ADS_1CLICK) {
    provider = AdProvider.ROLLER_ADS;
    experimentKeywordClick = true;
  } else if (landerConfig.lander?.feedID === FeedType.ROLLER_ADS) {
    provider = AdProvider.ROLLER_ADS;
  } else if (inRollerAdsTreatment) {
    provider = AdProvider.ROLLER_ADS;
    experimentKeywordClick = true;
  } else {
    provider = AdProvider.YAHOO;
  }

  return { provider, rsocUrl, rsocError, experimentKeywordClick };
}
